import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/hooks/trpc";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  HarvestOperationTemplateRevisionId,
  HarvestOperationTemplateAttributeId,
  TestTemplateId,
  TestTemplateAttributeId,
  type AttributeKindEnum,
} from "@brewbase/db/types";
import { Copy } from "lucide-react";

type TemplateType = "harvest" | "test";

interface CopyAttributesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  templateType: TemplateType;
  // For harvest: the current draft revision id. For test: the top-level test_template id.
  targetTemplateId: HarvestOperationTemplateRevisionId | TestTemplateId;
}

type TemplateOption = {
  id: HarvestOperationTemplateRevisionId | TestTemplateId;
  name: string;
  version: string | null;
};

type AttributeOption = {
  id: HarvestOperationTemplateAttributeId | TestTemplateAttributeId;
  name: string;
  kind: AttributeKindEnum;
  unit: string | null;
  description: string | null;
  isParameter: boolean;
  is_control_point?: boolean; // Optional since test templates don't have control points
  is_required?: boolean; // Only for test templates
};

export function CopyAttributesDialog({
  isOpen,
  onClose,
  templateType,
  targetTemplateId,
}: CopyAttributesDialogProps) {
  // For harvest, selectedTemplateId holds a *revision* id (the draft of the
  // chosen source variant). For test, it holds a top-level test_template id.
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    HarvestOperationTemplateRevisionId | TestTemplateId | null
  >(null);
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<
    Set<HarvestOperationTemplateAttributeId | TestTemplateAttributeId>
  >(new Set());
  const utils = trpc.useUtils();

  // Fetch all templates except the target one
  const { data: harvestTemplates = [], isLoading: isLoadingHarvestTemplates } =
    trpc.harvest.operationTemplates.templatesList.useQuery(undefined, {
      enabled: templateType === "harvest",
    });

  const { data: testTemplates = [], isLoading: isLoadingTestTemplates } =
    trpc.tests.testTemplates.list.useQuery(undefined, {
      enabled: templateType === "test",
    });

  const isLoadingTemplates =
    templateType === "harvest"
      ? isLoadingHarvestTemplates
      : isLoadingTestTemplates;

  // Each template exposes its draft revision id directly; use those as the
  // source options (the copy operates on the source draft revision).
  const sourceTemplates: TemplateOption[] = useMemo(() => {
    if (templateType === "harvest") {
      const opts: TemplateOption[] = [];
      for (const template of harvestTemplates) {
        if (!template.draft_revision_id) continue;
        if (template.draft_revision_id === targetTemplateId) continue;
        opts.push({
          id: template.draft_revision_id,
          name: template.name,
          version: null,
        });
      }
      return opts;
    }
    return testTemplates
      .filter((t) => t.id !== targetTemplateId)
      .map((t) => ({ id: t.id, name: t.name, version: null }));
  }, [harvestTemplates, testTemplates, templateType, targetTemplateId]);

  // Fetch attributes for the selected template / revision
  const {
    data: harvestAttributes = [],
    isLoading: isLoadingHarvestAttributes,
  } = trpc.harvest.operationTemplates.attributeList.useQuery(
    selectedTemplateId! as HarvestOperationTemplateRevisionId,
    { enabled: !!selectedTemplateId && templateType === "harvest" },
  );

  const { data: testAttributes = [], isLoading: isLoadingTestAttributes } =
    trpc.tests.testTemplates.attributeList.useQuery(
      selectedTemplateId! as TestTemplateId,
      { enabled: !!selectedTemplateId && templateType === "test" },
    );

  const isLoadingAttributes =
    templateType === "harvest"
      ? isLoadingHarvestAttributes
      : isLoadingTestAttributes;

  const attributeOptions: AttributeOption[] = useMemo(() => {
    if (templateType === "harvest") {
      return harvestAttributes.map((attr) => ({
        id: attr.id,
        name: attr.name,
        kind: attr.kind,
        unit: attr.measurement_default_unit,
        description: attr.description,
        isParameter: attr.role === "PARAMETER",
        is_control_point: attr.is_control_point,
      }));
    }
    return testAttributes.map((attr) => ({
      id: attr.id,
      name: attr.name,
      kind: attr.kind,
      unit: attr.measurement_default_unit,
      description: attr.description,
      isParameter: attr.phase !== null,
      is_required: attr.is_specification,
    }));
  }, [harvestAttributes, testAttributes, templateType]);

  const copyHarvestAttributesMutation =
    trpc.harvest.operationTemplates.copyAttributesFromRevision.useMutation({
      onSuccess: () => {
        toast.success(
          `Successfully copied ${selectedAttributeIds.size} attributes`,
        );
        utils.harvest.operationTemplates.attributeList.invalidate(
          targetTemplateId as HarvestOperationTemplateRevisionId,
        );
        handleClose();
      },
      onError: (error) => {
        toast.error(`Failed to copy attributes: ${error.message}`);
      },
    });

  const copyTestAttributesMutation =
    trpc.tests.testTemplates.copyAttributesFromTemplate.useMutation({
      onSuccess: () => {
        toast.success(
          `Successfully copied ${selectedAttributeIds.size} attributes`,
        );
        utils.tests.testTemplates.attributeList.invalidate(
          targetTemplateId as TestTemplateId,
        );
        handleClose();
      },
      onError: (error) => {
        toast.error(`Failed to copy attributes: ${error.message}`);
      },
    });

  const copyAttributesMutation =
    templateType === "harvest"
      ? copyHarvestAttributesMutation
      : copyTestAttributesMutation;

  const handleClose = () => {
    setSelectedTemplateId(null);
    setSelectedAttributeIds(new Set());
    onClose();
  };

  const handleTemplateSelect = (templateId: string) => {
    const id =
      templateType === "harvest"
        ? (parseInt(templateId) as HarvestOperationTemplateRevisionId)
        : (parseInt(templateId) as TestTemplateId);
    setSelectedTemplateId(id);
    setSelectedAttributeIds(new Set()); // Reset selected attributes
  };

  const handleAttributeToggle = (
    attributeId: HarvestOperationTemplateAttributeId | TestTemplateAttributeId,
    checked: boolean,
  ) => {
    const newSelected = new Set(selectedAttributeIds);
    if (checked) {
      newSelected.add(attributeId);
    } else {
      newSelected.delete(attributeId);
    }
    setSelectedAttributeIds(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedAttributeIds(new Set(attributeOptions.map((attr) => attr.id)));
  };

  const handleUnselectAll = () => {
    setSelectedAttributeIds(new Set());
  };

  const handleCopy = () => {
    if (!selectedTemplateId || selectedAttributeIds.size === 0) return;

    if (templateType === "harvest") {
      copyHarvestAttributesMutation.mutate({
        sourceRevisionId: selectedTemplateId as HarvestOperationTemplateRevisionId,
        targetRevisionId: targetTemplateId as HarvestOperationTemplateRevisionId,
        attributeIds: Array.from(
          selectedAttributeIds,
        ) as HarvestOperationTemplateAttributeId[],
      });
    } else {
      copyTestAttributesMutation.mutate({
        sourceTemplateId: selectedTemplateId as TestTemplateId,
        targetTemplateId: targetTemplateId as TestTemplateId,
        attributeIds: Array.from(
          selectedAttributeIds,
        ) as TestTemplateAttributeId[],
      });
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplateId(null);
      setSelectedAttributeIds(new Set());
    }
  }, [isOpen]);

  const canCopy = selectedTemplateId && selectedAttributeIds.size > 0;
  const allSelected =
    attributeOptions.length > 0 &&
    selectedAttributeIds.size === attributeOptions.length;
  const someSelected = selectedAttributeIds.size > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Copy Attributes from Another{" "}
            {templateType === "harvest" ? "Harvest Operation" : "Test"} Template
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-4 overflow-hidden">
          {/* Template Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Source Template</label>
            <Select
              value={selectedTemplateId?.toString() || ""}
              onValueChange={(value) => {
                if (value) {
                  handleTemplateSelect(value);
                }
              }}
              disabled={isLoadingTemplates}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a template to copy from..." />
              </SelectTrigger>
              <SelectContent>
                {sourceTemplates.map((template) => (
                  <SelectItem key={template.id} value={template.id.toString()}>
                    {template.name}
                    {template.version ? (
                      <span className="text-muted-foreground">
                        {template.version}
                      </span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Attributes Selection */}
          {selectedTemplateId && (
            <div className="flex-1 flex flex-col gap-2 overflow-hidden">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">
                  Attributes to Copy ({selectedAttributeIds.size} selected)
                </label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={allSelected || isLoadingAttributes}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUnselectAll}
                    disabled={!someSelected || isLoadingAttributes}
                  >
                    Unselect All
                  </Button>
                </div>
              </div>

              <ScrollArea className="flex-1 border rounded-md overflow-y-auto">
                <div className="p-4 space-y-3">
                  {isLoadingAttributes ? (
                    <p className="text-muted-foreground">
                      Loading attributes...
                    </p>
                  ) : attributeOptions.length === 0 ? (
                    <p className="text-muted-foreground">
                      No attributes found in this template.
                    </p>
                  ) : (
                    attributeOptions.map((attribute) => (
                      <div
                        key={attribute.id}
                        className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={selectedAttributeIds.has(attribute.id)}
                          onCheckedChange={(checked) =>
                            handleAttributeToggle(
                              attribute.id,
                              checked as boolean,
                            )
                          }
                          className="mt-1"
                        />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {attribute.name}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {attribute.kind}
                            </Badge>
                            {attribute.unit && (
                              <Badge variant="outline" className="text-xs">
                                {attribute.unit}
                              </Badge>
                            )}
                          </div>

                          <div className="flex gap-2">
                            {attribute.isParameter && (
                              <Badge variant="outline" className="text-xs">
                                Parameter
                              </Badge>
                            )}
                            {attribute.is_control_point && (
                              <Badge variant="outline" className="text-xs">
                                Control Point
                              </Badge>
                            )}
                            {attribute.is_required && (
                              <Badge variant="outline" className="text-xs">
                                Required
                              </Badge>
                            )}
                          </div>

                          {attribute.description && (
                            <p className="text-sm text-muted-foreground">
                              {attribute.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCopy}
            disabled={!canCopy || copyAttributesMutation.isPending}
          >
            {copyAttributesMutation.isPending
              ? "Copying..."
              : `Copy ${selectedAttributeIds.size} Attributes`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
